// "Inventar bez muke" (CHANGELOG 6.4): automatic dismantling of new loot and the
// "Equip best" plan. Both are pure so the server applies exactly what the tests check.

import { canWear, findItemTemplate, isSlotLocked } from '../content/items';
import { deriveStats, type SurvivorBuild } from '../formulas';
import { GEAR_SLOTS, type Equipment, type GearSlot, type ItemInstance, type Rarity } from '../types';
import type { LoadInfo } from './load';

export const AUTO_DISMANTLE_RARITIES = ['common', 'uncommon', 'rare', 'epic'] as const satisfies readonly Rarity[];
export type AutoDismantleRarity = (typeof AUTO_DISMANTLE_RARITIES)[number];

/** A rarity's highest tier that goes: 0 is off for that rarity, N takes items of tier N or lower. */
export const AUTO_DISMANTLE_TIERS = [0, 1, 2, 3, 4] as const;
export type AutoDismantleTier = (typeof AUTO_DISMANTLE_TIERS)[number];
/** A missing rarity is off, like 0. */
export type AutoDismantleTiers = Partial<Record<AutoDismantleRarity, AutoDismantleTier>>;

/**
 * Which new loot gets dismantled (on claim and, with PACK_SWAP, out on the run). Legendary items and locked
 * items never are. 9. 10. 2026 (owner): a highest tier per rarity (Common up to T4, Uncommon up to T2, Rare
 * off …) instead of one tier for all the rarities picked.
 */
export interface AutoDismantleRule {
  enabled: boolean;
  tiers: AutoDismantleTiers;
}

/** The rule before 9. 10. 2026: the rarities picked, all up to one tier. Old rows (before migration 0021) and old clients. */
export interface LegacyAutoDismantleRule {
  enabled: boolean;
  rarities: AutoDismantleRarity[];
  /** Only items of this tier or lower. */
  maxTier: 1 | 2 | 3 | 4;
}

/** Off, and Common up to T2 once it is turned on (the default before 9. 10. 2026 too). */
export const DEFAULT_AUTO_DISMANTLE: AutoDismantleRule = { enabled: false, tiers: { common: 2 } };

const tierOf = (v: unknown): AutoDismantleTier => (typeof v === 'number' && Number.isFinite(v) ? (Math.max(0, Math.min(4, Math.floor(v))) as AutoDismantleTier) : 0);

/**
 * The rule in its one shape, whatever was saved or sent: the old `rarities` + `maxTier` gives each rarity picked
 * that tier and the others off (the same items go as before). Rarities that are off are left out, unknown keys
 * and Legendary are dropped. Read the rule through this (or `shouldAutoDismantle`), never its fields directly.
 */
export function normalizeAutoDismantle(rule: AutoDismantleRule | LegacyAutoDismantleRule | null | undefined): AutoDismantleRule {
  if (!rule || typeof rule !== 'object') return { enabled: DEFAULT_AUTO_DISMANTLE.enabled, tiers: { ...DEFAULT_AUTO_DISMANTLE.tiers } };
  const r = rule as Partial<AutoDismantleRule & LegacyAutoDismantleRule>;
  const tiers: AutoDismantleTiers = {};
  if (r.tiers && typeof r.tiers === 'object') {
    for (const k of AUTO_DISMANTLE_RARITIES) {
      const n = tierOf(r.tiers[k]);
      if (n > 0) tiers[k] = n;
    }
  } else if (Array.isArray(r.rarities)) {
    const n = tierOf(r.maxTier ?? 2) || 1;
    for (const k of AUTO_DISMANTLE_RARITIES) if (r.rarities.includes(k)) tiers[k] = n;
  }
  return { enabled: r.enabled === true, tiers };
}

/** The rarities the rule takes apart (tier 1 or more), Common first. */
export function autoDismantleRarities(rule: AutoDismantleRule | LegacyAutoDismantleRule | null | undefined): AutoDismantleRarity[] {
  const { tiers } = normalizeAutoDismantle(rule);
  return AUTO_DISMANTLE_RARITIES.filter((k) => (tiers[k] ?? 0) > 0);
}

export function shouldAutoDismantle(rule: AutoDismantleRule | LegacyAutoDismantleRule | null | undefined, item: { templateId: string; rarity: Rarity; locked?: boolean; tier?: number | null }): boolean {
  if (!rule?.enabled || item.locked || item.rarity === 'legendary') return false;
  const max = normalizeAutoDismantle(rule).tiers[item.rarity as AutoDismantleRarity] ?? 0;
  if (max <= 0) return false;
  const t = findItemTemplate(item.templateId);
  if (!t) return false;
  return (item.tier ?? t.tier) <= max;
}

// ---- Krug 5 (K7.1): dismantling many items at once --------------------------------------------

/** Why an item is never dismantled in bulk (the server checks each id again). */
export type BulkSkipReason = 'equipped' | 'locked' | 'upgrading' | 'account_bound' | 'legendary' | 'away' | 'not_found';

export const BULK_SKIP_ORDER: readonly BulkSkipReason[] = ['equipped', 'locked', 'upgrading', 'account_bound', 'legendary', 'away', 'not_found'];

/** Most ids in one bulk call. */
export const BULK_DISMANTLE_MAX = 300;

/** An item that must stay, and why; null when it may go. The pack only counts while the survivor is at camp. */
export function bulkDismantleSkip(
  item: { id: string; location: string; locked: boolean; rarity: Rarity; accountBound: boolean },
  ctx: { upgrading: ReadonlySet<string> | readonly string[]; away: boolean },
): BulkSkipReason | null {
  const upgrading = ctx.upgrading instanceof Set ? ctx.upgrading : new Set(ctx.upgrading as readonly string[]);
  if (item.location === 'equipped') return 'equipped';
  if (item.locked) return 'locked';
  if (upgrading.has(item.id)) return 'upgrading';
  if (item.accountBound) return 'account_bound';
  if (item.rarity === 'legendary') return 'legendary';
  if (item.location === 'pack' && ctx.away) return 'away';
  return null;
}

export interface BulkDismantleFilter {
  rarities: (typeof AUTO_DISMANTLE_RARITIES)[number][];
  /** Items of this tier or lower. */
  maxTier: 1 | 2 | 3 | 4;
  slot: GearSlot | 'all';
  /** Only items with less PWR than what is worn in their slot (an empty slot keeps its items). */
  weakerThanWorn: boolean;
  /** Of each item (template) the best copy stays: by PWR, then rarity. */
  keepBest: boolean;
}

export const DEFAULT_BULK_FILTER: BulkDismantleFilter = { rarities: ['common', 'uncommon'], maxTier: 4, slot: 'all', weakerThanWorn: false, keepBest: false };

export interface BulkItem {
  id: string;
  templateId: string;
  slot: GearSlot;
  tier: number;
  rarity: Rarity;
  power: number;
  location: string;
  locked: boolean;
  accountBound: boolean;
}

const RANK: Record<Rarity, number> = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4 };

/**
 * The items the filters pick, and how many that match them must stay (by reason), for the
 * preview line "3 locked items kept". Worn, locked, upgrading, account-bound and Legendary items
 * never go, nor the pack while the survivor is out.
 */
export function bulkDismantlePick<T extends BulkItem>(items: readonly T[], f: BulkDismantleFilter, ctx: { upgrading: ReadonlySet<string> | readonly string[]; away: boolean }): { pick: T[]; kept: Partial<Record<BulkSkipReason, number>> } {
  const worn = new Map<GearSlot, T>();
  for (const it of items) if (it.location === 'equipped') worn.set(it.slot, it);
  const best = new Map<string, T>();
  for (const it of items) {
    const b = best.get(it.templateId);
    if (!b || it.power > b.power || (it.power === b.power && RANK[it.rarity] > RANK[b.rarity])) best.set(it.templateId, it);
  }
  const pick: T[] = [];
  const kept: Partial<Record<BulkSkipReason, number>> = {};
  for (const it of items) {
    if (!(f.rarities as Rarity[]).includes(it.rarity) || it.tier > f.maxTier || (f.slot !== 'all' && it.slot !== f.slot)) continue;
    const why = bulkDismantleSkip(it, ctx);
    if (why) {
      if (why !== 'equipped') kept[why] = (kept[why] ?? 0) + 1;
      continue;
    }
    if (f.weakerThanWorn) {
      const w = worn.get(it.slot);
      if (!w || it.power >= w.power) continue;
    }
    if (f.keepBest && best.get(it.templateId)?.id === it.id) continue;
    pick.push(it);
  }
  return { pick, kept };
}

// ---- The survivor's Power with other gear (30. 9. 2026) -------------------------------------------

/**
 * The survivor's stats with this equipment: level, attributes, skills, sets, perks and load. The one
 * measure that Equip Best, the slot picker's order and "Best", the item detail's "Your Power" and the
 * upgrade preview compare gear by (an item's PWR is measured on a reference survivor of its own tier,
 * so it only compares gear of the same tier).
 */
export function survivorStatsWith(build: SurvivorBuild, equipment: Equipment) {
  return deriveStats({ ...build, equipment });
}

/** The survivor's Power now and with `changes` on (an item in a slot, or null to empty it). */
export function powerChange(build: SurvivorBuild, changes: Partial<Record<GearSlot, ItemInstance | null>>): { before: number; after: number; delta: number; pct: number } {
  const before = survivorStatsWith(build, build.equipment).power;
  const equipment: Equipment = { ...build.equipment };
  for (const [slot, it] of Object.entries(changes) as [GearSlot, ItemInstance | null][]) {
    if (it) equipment[slot] = it;
    else delete equipment[slot];
  }
  const after = survivorStatsWith(build, equipment).power;
  return { before, after, delta: after - before, pct: before > 0 ? (after - before) / before : 0 };
}

export interface EquipChange {
  slot: GearSlot;
  /** Item taken off (null: the slot was empty). */
  from: string | null;
  /** Item put on (null: the slot is emptied, only to get under the blocked load). */
  to: string | null;
}

export interface EquipBestPlan {
  changes: EquipChange[];
  powerBefore: number;
  powerAfter: number;
  loadBefore: LoadInfo;
  loadAfter: LoadInfo;
  /** The new gear is Overloaded (allowed, but worth a warning). */
  overloaded: boolean;
}

/**
 * Best gear by survivor Power (set bonuses included), never ending in the
 * `blocked` load state. Slot by slot, keep the item that raises Power the most;
 * repeat until nothing improves. Ties keep what is already worn.
 */
export function bestEquipment(build: SurvivorBuild, owned: ItemInstance[]): EquipBestPlan {
  const bySlot = new Map<GearSlot, ItemInstance[]>();
  for (const it of owned) {
    const slot = findItemTemplate(it.templateId)?.slot;
    // Items above the survivor's level wait (krug 5, F8.4); one already worn stays until something beats it. 23. 10. 2026
    // (BACKLOG #55): by the item's own tier (a tier upgrade), and one tier up is worn as the top of the survivor's tier
    // (`deriveStats` counts it so; Power decides as for any item).
    if (!slot || isSlotLocked(slot) || !canWear(build.level, it.templateId, it.tier)) continue;
    bySlot.set(slot, [...(bySlot.get(slot) ?? []), it]);
  }
  const statsOf = (equipment: Equipment) => survivorStatsWith(build, equipment);
  const score = (equipment: Equipment) => {
    const s = statsOf(equipment);
    return s.load?.state === 'blocked' ? -Infinity : s.power;
  };
  const before = statsOf(build.equipment);
  let eq: Equipment = { ...build.equipment };
  let current = score(eq);
  for (let pass = 0; pass < 4; pass++) {
    let improved = false;
    for (const slot of GEAR_SLOTS) {
      if (isSlotLocked(slot)) continue;
      for (const cand of bySlot.get(slot) ?? []) {
        if (eq[slot]?.id === cand.id) continue;
        const next = { ...eq, [slot]: cand };
        const sc = score(next);
        if (sc > current + 1e-9) {
          eq = next;
          current = sc;
          improved = true;
        }
      }
      // Still too heavy to leave: taking something off is also a move.
      if (current === -Infinity && eq[slot]) {
        const next = { ...eq };
        delete next[slot];
        const sc = score(next);
        if (sc > current) {
          eq = next;
          current = sc;
          improved = true;
        }
      }
    }
    if (!improved) break;
  }
  const after = statsOf(eq);
  const changes: EquipChange[] = [];
  for (const slot of GEAR_SLOTS) {
    const was = build.equipment[slot]?.id ?? null;
    const now = eq[slot]?.id ?? null;
    if (was !== now) changes.push({ slot, from: was, to: now });
  }
  return {
    changes,
    powerBefore: before.power,
    powerAfter: after.power,
    loadBefore: before.load!,
    loadAfter: after.load!,
    overloaded: after.load?.state === 'overloaded',
  };
}
